
import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  type?: string;
  jsonLd?: object;
}

export default function SEOHead({ 
  title, 
  description, 
  keywords, 
  canonicalPath,
  type = 'WebPage',
  jsonLd 
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://crownabundance.com'}${canonicalPath}`);
    }

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    // Remove existing JSON-LD
    const existingJsonLd = document.querySelector('script[data-seo-jsonld]');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    // Add JSON-LD structured data
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://crownabundance.com';
    const defaultJsonLd = {
      '@context': 'https://schema.org',
      '@type': type,
      'name': title,
      'description': description,
      'url': `${baseUrl}${canonicalPath}`,
      'publisher': {
        '@type': 'Organization',
        'name': 'The CROWN Abundance Movement',
        'logo': {
          '@type': 'ImageObject',
          'url': `${baseUrl}/logo.png`
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(jsonLd || defaultJsonLd);
    document.head.appendChild(script);

    return () => {
      const jsonLdScript = document.querySelector('script[data-seo-jsonld]');
      if (jsonLdScript) {
        jsonLdScript.remove();
      }
    };
  }, [title, description, keywords, canonicalPath, type, jsonLd]);

  return null;
}
